import { crudOperations } from './api';

class UserService {
  // Get all users (admin only)
  async getUsers({ page = 1, limit = 50, role, active, search } = {}) {
    try {
      const params = { page, limit };
      if (role) params.role = role;
      if (active !== undefined) params.active = active;
      if (search) params.search = search;

      const response = await crudOperations.get('/users', params);
      return response.data;
    } catch (error) {
      console.error('Get users error:', error);
      throw error;
    }
  }

  // Get user by ID
  async getUser(userId) {
    try {
      const response = await crudOperations.get(`/users/${userId}`);
      return response.data.user;
    } catch (error) {
      console.error('Get user error:', error);
      throw error;
    }
  }

  // Create new user (admin only)
  async createUser(userData) {
    try {
      const response = await crudOperations.post('/users', userData);
      return response.data;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  }

  // Update user
  async updateUser(userId, userData) {
    try {
      const response = await crudOperations.put(`/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Update user error:', error);
      throw error;
    }
  }

  // Delete user (admin only)
  async deleteUser(userId) {
    try {
      const response = await crudOperations.delete(`/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Delete user error:', error);
      throw error;
    }
  }

  // Get franchisees list (for assigning technicians)
  async getFranchisees() {
    try {
      const response = await crudOperations.get('/users/franchisees/list');
      return response.data.franchisees;
    } catch (error) {
      console.error('Get franchisees error:', error);
      throw error;
    }
  }

  // Get covered areas (post towns) for all users
  async getCoveredAreas() {
    try {
      const response = await crudOperations.get('/users/covered-areas');
      return response.data.coveredAreas;
    } catch (error) {
      console.error('Get covered areas error:', error);
      return {};
    }
  }

  // Generate random secure password
  generateSecurePassword(length = 12) {
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    // Ensure at least one of each required type
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)]; // lowercase
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)]; // uppercase
    password += '0123456789'[Math.floor(Math.random() * 10)]; // number
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)]; // special
    
    // Fill remaining length
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  // Validate user data before submission
  validateUserData(userData, isUpdate = false) {
    const errors = [];

    // Email validation
    if (!isUpdate || userData.email) {
      if (!userData.email) {
        errors.push('Email is required');
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
        errors.push('Please provide a valid email address');
      }
    }

    // Name validation
    if (!isUpdate || userData.name) {
      if (!userData.name || userData.name.trim().length < 2) {
        errors.push('Name must be at least 2 characters long');
      }
    }

    // Role validation
    if (!isUpdate || userData.role) {
      const validRoles = ['admin', 'franchisee', 'technician'];
      if (!userData.role || !validRoles.includes(userData.role)) {
        errors.push('Role must be admin, franchisee, or technician');
      }
    }

    // Password validation (only for new users)
    if (!isUpdate && userData.password) {
      if (userData.password.length < 8) {
        errors.push('Password must be at least 8 characters long');
      }
      if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(userData.password)) {
        errors.push('Password must contain uppercase, lowercase, and number');
      }
    }

    // Phone validation (optional)
    if (userData.phone && !/^[\+]?[1-9][\d]{0,15}$/.test(userData.phone)) {
      errors.push('Please provide a valid phone number');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Format user data for display
  formatUserForDisplay(user) {
    return {
      ...user,
      displayName: user.name || user.email,
      roleDisplay: this.formatRole(user.role),
      statusDisplay: user.is_active ? 'Active' : 'Inactive',
      createdAtDisplay: user.created_at ? new Date(user.created_at).toLocaleDateString() : 'Unknown'
    };
  }

  // Format role for display
  formatRole(role) {
    const roleMap = {
      admin: 'Administrator',
      franchisee: 'Franchisee',
      technician: 'Technician'
    };
    return roleMap[role] || role;
  }

  // Check if user can be deleted
  canDeleteUser(user, currentUser) {
    // Cannot delete yourself
    if (user.id === currentUser.id) {
      return { canDelete: false, reason: 'Cannot delete your own account' };
    }

    // Cannot delete the last admin
    if (user.role === 'admin') {
      // This would need an additional API call to check admin count
      // For now, we'll allow it and let the backend handle the validation
    }

    return { canDelete: true };
  }

  // Get user initials for avatar
  getUserInitials(user) {
    if (!user || !user.name) return '?';
    
    const names = user.name.trim().split(' ');
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }

  // Sort users by various criteria
  sortUsers(users, sortBy = 'name', sortOrder = 'asc') {
    return [...users].sort((a, b) => {
      let aValue = a[sortBy];
      let bValue = b[sortBy];

      // Handle dates
      if (sortBy.includes('_at')) {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      }

      // Handle strings
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
      }
    });
  }

  // Filter users by search term
  filterUsers(users, searchTerm) {
    if (!searchTerm) return users;

    const search = searchTerm.toLowerCase();
    return users.filter(user =>
      user.name?.toLowerCase().includes(search) ||
      user.email?.toLowerCase().includes(search) ||
      user.phone?.includes(search) ||
      user.role?.toLowerCase().includes(search)
    );
  }
}

// Create singleton instance
const userService = new UserService();

export default userService;